import { useState, useEffect, useCallback } from 'react';
import { buildingChecksApi } from '../../api/buildingChecksApi';
import type { DateInterval } from '../../Helpers/DateUtils';
import type { BuildingCheckTemplate } from '../../Pages/BuildingChecks/types';
import type { BuildingCheck } from '../../Pages/BuildingChecks/buildingChecksUtils';

interface UseComponentChecksReturn {
    /** The building's checks, keyed by check key */
    instances: Record<string, BuildingCheck>;
    /** Every check template, keyed by ID */
    templates: Record<string, BuildingCheckTemplate>;
    loading: boolean;
    error: string | null;
    clearError: () => void;
    /** Reload after a template was created elsewhere (the draft's "Skapa ny") */
    reload: () => Promise<void>;
    /** Create one check of the template per component, in one write */
    createChecks: (template: BuildingCheckTemplate, componentKeys: string[], settings: NewComponentCheck) => Promise<boolean>;
    /** Remove the checks from the building (admins); their completions are kept */
    removeChecks: (checkKeys: string[]) => Promise<boolean>;
    setNextDueDate: (checkKeys: string[], nextDueDate: string) => Promise<boolean>;
}

/** What the draft asks for when it creates checks for components (the same for each) */
export interface NewComponentCheck {
    firstDueDate: string;
    /** Stored as customInterval only when it differs from the template's */
    interval: DateInterval;
    /** Only offered for a single component; ignored for several */
    label: string;
}

const sameInterval = (a: DateInterval, b: DateInterval) => a.value === b.value && a.unit === b.unit;

/**
 * A building's checks and check templates for the maintenance draft's
 * Kontroller rows. Loaded once and reloaded after every change.
 */
export const useComponentChecks = (buildingKey: string | undefined): UseComponentChecksReturn => {
    const [instances, setInstances] = useState<Record<string, BuildingCheck>>({});
    const [templates, setTemplates] = useState<Record<string, BuildingCheckTemplate>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const reload = useCallback(async () => {
        if (!buildingKey) return;
        try {
            const [loadedInstances, loadedTemplates] = await Promise.all([
                buildingChecksApi.loadBuildingCheckInstances(buildingKey),
                buildingChecksApi.loadTemplates()
            ]);
            setInstances(loadedInstances);
            setTemplates(loadedTemplates);
        } catch (err) {
            console.error('Error loading building checks:', err);
            setError('Kunde inte ladda kontroller');
        } finally {
            setLoading(false);
        }
    }, [buildingKey]);

    useEffect(() => {
        setLoading(true);
        void reload();
    }, [reload]);

    // One check per component (Massredigering: every selected one); the list is reloaded once
    const createChecks = useCallback(async (template: BuildingCheckTemplate, componentKeys: string[], settings: NewComponentCheck) => {
        if (!buildingKey || componentKeys.length === 0) return false;
        const label = componentKeys.length === 1 ? settings.label.trim() : '';
        try {
            await buildingChecksApi.createBuildingChecks(buildingKey, componentKeys.map(componentKey => ({
                templateId: template.id,
                startDate: settings.firstDueDate,
                nextDueDate: settings.firstDueDate,
                componentKey,
                ...(label && { label }),
                ...(!sameInterval(settings.interval, template.interval) && { customInterval: settings.interval })
            })));
            await reload();
            return true;
        } catch (err) {
            console.error('Error creating checks for components:', err);
            setError(err instanceof Error ? err.message : 'Kunde inte skapa kontrollen');
            return false;
        }
    }, [buildingKey, reload]);

    const removeChecks = useCallback(async (checkKeys: string[]) => {
        if (!buildingKey) return false;
        try {
            await buildingChecksApi.removeBuildingChecks(buildingKey, checkKeys);
            await reload();
            return true;
        } catch (err) {
            console.error('Error removing checks:', err);
            setError(err instanceof Error ? err.message : 'Kunde inte ta bort kontrollen');
            await reload();
            return false;
        }
    }, [buildingKey, reload]);

    const setNextDueDate = useCallback(async (checkKeys: string[], nextDueDate: string) => {
        if (!buildingKey) return false;
        try {
            await buildingChecksApi.setNextDueDates(buildingKey, checkKeys, nextDueDate);
            await reload();
            return true;
        } catch (err) {
            console.error('Error moving check due date:', err);
            setError(err instanceof Error ? err.message : 'Kunde inte ändra datumet');
            return false;
        }
    }, [buildingKey, reload]);

    return {
        instances,
        templates,
        loading,
        error,
        clearError: () => setError(null),
        reload,
        createChecks,
        removeChecks,
        setNextDueDate
    };
};
